/* eslint-disable max-len */
import React, { ReactElement, useState } from 'react';
import { isEmpty } from 'ts-utils';
import { INSURANCE_PROVIDERS_AU } from 'hydra-data-types';
import { LayoutScreenConfig, ScreenType } from '../../config/FlowTypes';
import {
  INSURANCE_PROVIDERS_US_MEDICARE_FIRST,
  NOT_LISTED_OPTION,
  getInsuranceDiscount,
} from '../../config/InsuranceProviders';
import { ActionType } from '../../store/ActionTypes';
import { store, StoreStateInternal, useAppStore } from '../../store/Store';
import { dispatchUserData } from '../../store/userData/UserDataActions';
import { Analytics } from '../../util/Analytics';
import { getCoverageCheck, getInsuranceProviderUserData } from '../../util/InsuranceCoverageCheck';
import { isEligibleForFree65Access } from '../../util/MedicareAccess';
import { getPartnerInsurer } from '../../util/PartnerInsurers';
import { ScreenWrapper } from '../styles/Containers';
import { NavigationProps } from './FlowNavigation';
import {
  CoverageCheckConsentValues,
  CoverageCheckConsents,
  CoverageCheckLayoutConfig,
  areCoverageConsentsGiven,
} from './layouts/CoverageCheckConsents';
import { DropdownOption, DropdownSelectLayout } from './layouts/DropdownSelectLayout';
import { LayoutActions } from './layouts/LayoutActions';

export type InsuranceProviderLayoutConfig = {
  title?: string;
  question: string;
  primaryButtonTitle: string;
  /** Overrides the geolocation-based provider list (e.g. a Medicare Advantage plan list). */
  providers?: readonly string[];
  /**
   * Whether selecting a provider applies the insurance discount coupon. Defaults to true. Set false
   * on referral flows that already carry their own coupon in the URL, so it isn't overwritten.
   */
  applyInsuranceCoupon?: boolean;
  /**
   * "Check my coverage" variant for US non-65+ users (the coverage-flow audience): replaces the copy
   * and button title, requires two consents, and adds a self-pay exit that skips the
   * coverage screens. Ignored for everyone else, who see the plain dropdown.
   */
  coverageCheck?: CoverageCheckLayoutConfig;
  /** The same for AU users: one consent, plus an "I don't have private health insurance" exit. */
  auCoverageCheck?: CoverageCheckLayoutConfig;
};

const CONSENT_YES = 'Yes';

export type InsuranceProviderScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.InsuranceProvider,
  InsuranceProviderLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  InsuranceProviderScreenConfig<ScreenNames>,
  ScreenNames
>;

export function getInsuranceProvidersForCountry(country: string): readonly string[] {
  return country === 'AU' ? INSURANCE_PROVIDERS_AU : INSURANCE_PROVIDERS_US_MEDICARE_FIRST;
}

/**
 * The insurance discount a submit applies, if any. Screens that opt out leave the coupon already in
 * pricing state untouched (e.g. the referral coupon from the URL's `?coupon=` param).
 */
export function getInsuranceProviderCouponCode(
  state: StoreStateInternal,
  layout: InsuranceProviderLayoutConfig
): string | undefined {
  return layout.applyInsuranceCoupon !== false && isEligibleForFree65Access(state) === false
    ? getInsuranceDiscount(state).couponCode
    : undefined;
}

export const InsuranceProviderScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const { navigation, screenName, layout } = props;
  const country = useAppStore((state) => state.entry.country);
  const insuranceProviders = layout.providers ?? getInsuranceProvidersForCountry(country);
  const coverageCheck = useAppStore((state) => getCoverageCheck(state, layout));
  const storedAuthorization = useAppStore(
    (state) => state.userData.coverageCheckAuthorization?.[0] === CONSENT_YES
  );
  const storedAssignment = useAppStore(
    (state) => state.userData.coverageAssignmentOfBenefits?.[0] === CONSENT_YES
  );
  const [consents, setConsents] = useState<CoverageCheckConsentValues>({
    authorization: storedAuthorization,
    assignmentOfBenefits: storedAssignment,
  });

  const options: DropdownOption[] = [
    { value: 'separator', label: '──────────', disabled: true },
    ...insuranceProviders,
    NOT_LISTED_OPTION,
  ];

  // Local state to track if button should be enabled
  const [selectedValue, setSelectedValue] = React.useState<string>('');

  // A partner insurer's members get its no-cost access offer next, so the check's consents and exits
  // don't apply.
  const shownCoverageCheck =
    getPartnerInsurer(selectedValue) === undefined ? coverageCheck : undefined;
  const consentsGiven =
    shownCoverageCheck === undefined || areCoverageConsentsGiven(shownCoverageCheck, consents);
  const noPrivateInsuranceTitle = shownCoverageCheck?.noPrivateInsuranceTitle;

  function onSelected(selected: string) {
    setSelectedValue(selected);
  }

  function trackAnswer(question: string, answer: string | string[]): void {
    Analytics.track({ event: Analytics.Event.o_question_answered, question, answer });
  }

  function onSubmitPress() {
    if (isEmpty(selectedValue) || !consentsGiven) return;

    const userData = getInsuranceProviderUserData(
      screenName,
      {
        action: 'submit',
        provider: selectedValue,
        consents: coverageCheck === undefined ? undefined : consents,
      },
      coverageCheck,
      store.getState().userData
    );
    dispatchUserData(userData);
    trackAnswer(screenName, [selectedValue]);
    if (userData.coverageCheckAuthorization !== undefined) {
      trackAnswer('coverageCheckAuthorization', userData.coverageCheckAuthorization[0]);
    }
    if (userData.coverageAssignmentOfBenefits !== undefined) {
      trackAnswer('coverageAssignmentOfBenefits', userData.coverageAssignmentOfBenefits[0]);
    }

    const couponCode = getInsuranceProviderCouponCode(store.getState(), layout);
    if (couponCode !== undefined) {
      store.dispatch({ type: ActionType.SET_COUPON_CODE, couponCode });
    }

    navigation.next(props);
  }

  /**
   * Self-pay link: records the choice (and the provider, if one was picked) and lets the
   * entry selector fall through to the config's non-coverage exit. No consents are needed, and any
   * given earlier is withdrawn so a user who went back to take this exit isn't filed for outreach.
   */
  function onSelfPayPress() {
    dispatchUserData(
      getInsuranceProviderUserData(
        screenName,
        { action: 'selfPay', provider: selectedValue },
        coverageCheck,
        store.getState().userData
      )
    );
    trackAnswer('coverageSelfPay', CONSENT_YES);
    if (!isEmpty(selectedValue)) trackAnswer(screenName, [selectedValue]);
    navigation.next(props);
  }

  /**
   * AU "I don't have private health insurance": records the choice and, like self-pay, withdraws any
   * earlier consent and falls through to the config's non-coverage exit. Any insurer picked first
   * contradicts it, so isn't stored.
   */
  function onNoPrivateInsurancePress() {
    dispatchUserData(
      getInsuranceProviderUserData(
        screenName,
        { action: 'noPrivateInsurance' },
        coverageCheck,
        store.getState().userData
      )
    );
    trackAnswer('coverageNoPrivateInsurance', CONSENT_YES);
    navigation.next(props);
  }

  return (
    <ScreenWrapper>
      <DropdownSelectLayout
        heading={coverageCheck?.title ?? layout.title}
        question={coverageCheck?.question ?? layout.question}
        options={options}
        onSelected={onSelected}
      />
      {shownCoverageCheck !== undefined && (
        <CoverageCheckConsents
          config={shownCoverageCheck}
          values={consents}
          onChange={setConsents}
        />
      )}
      <LayoutActions
        primaryButtonTitle={shownCoverageCheck?.primaryButtonTitle ?? layout.primaryButtonTitle}
        onPressPrimaryButton={onSubmitPress}
        primaryButtonDisabled={isEmpty(selectedValue) || !consentsGiven}
        secondaryButtonTitle={shownCoverageCheck?.selfPayTitle}
        onPressSecondaryButton={shownCoverageCheck === undefined ? undefined : onSelfPayPress}
        secondaryButtonPlacement="below"
        secondaryButtonTestId="coverage-self-pay"
        extraSecondaryButton={
          noPrivateInsuranceTitle === undefined
            ? undefined
            : {
                title: noPrivateInsuranceTitle,
                onPress: onNoPrivateInsurancePress,
                testId: 'coverage-no-private-insurance',
              }
        }
      />
    </ScreenWrapper>
  );
};
